import type { ConsentResponse } from "@/core/types";
import { isACPEligibleCarrier } from "@/lib/helpers/isACPEligibleCarrier";
import type { PatientState, RootState, ScheduleItem } from "@/lib/types/state";

/**
 * Determines if a patient is eligible for ACP (Advance Care Program) features
 * based on their primary insurance carrier.
 *
 * Patients with certain insurers (Other, CDPHP, MVP) are eligible for ACP features
 * including the Consents menu item.
 */
export const selectIsACPEligible = (state: RootState): boolean => {
  const carrier = state?.auth?.profile?.primary_insurance?.carrier;

  return carrier ? isACPEligibleCarrier(carrier) : false;
};

/**
 * Gets the advanced care consent response from the patient's consent data.
 * Returns the consent_response value from the first descendant (index 0) of
 * the advanced_care_consent object.
 */
export const selectSelfAcpConsentResponse = (state: RootState): ConsentResponse => {
  const advancedCareConsent = state?.auth?.profile?.consent?.advanced_care_program;

  if (
    !advancedCareConsent ||
    !Array.isArray(advancedCareConsent) ||
    advancedCareConsent.length === 0
  ) {
    return null;
  }

  // if the patient replied "Later" they will have a null answer in the array[0]
  // important to use the nullish coalescing operator here to preserve the null value
  return advancedCareConsent.at(-1)?.consent_response ?? null;
};

export const selectPrimaryInsuranceCarrier = (state: RootState) => {
  const carrier = state?.auth?.profile?.primary_insurance?.carrier;

  return carrier;
};

const patientSlice = (state: RootState): PatientState => state.patient;

export const selectAllowInactivePopUp = (state: RootState): boolean =>
  patientSlice(state)?.allowInactivePopUp ?? false;

export const selectAllowWaitListPopUp = (state: RootState): boolean =>
  patientSlice(state)?.allowWaitListPopUp ?? false;

export const selectScheduleList = (state: RootState): ScheduleItem[] =>
  patientSlice(state)?.scheduleList ?? [];
